<template>
  <v-row class="wrapper">
    <template v-if="isHistoryMode">
      <v-col cols="2">
        <div v-if="lastActionDate">
          <span class="date">{{ lastActionDate }}</span>
          <span class="date-label">
            {{ $t("message.externalChanges.actionPerformed") }}
          </span>
        </div>
        <div v-if="changeDetectedDate">
          <span class="date">{{ changeDetectedDate }}</span>
          <span class="date-label">
            {{ $t("message.externalChanges.changeDetected") }}
          </span>
        </div>
      </v-col>

      <v-col cols="2" v-if="change && change.source">
        {{ $t(`message.externalChangesSources.${change.source}`) }}
      </v-col>

      <v-col
        cols="2"
        v-if="change && change.status && Object.keys(change).includes('author')"
      >
        <p>{{ actionUpperLabel }}</p>
        <p v-if="change.author && change.author.email" class="author">
          {{ change.author.email }}
        </p>
        <p v-if="change.author === null" class="author">
          <v-icon color="primary" class="mb-1">mdi-shield-lock-outline</v-icon>
          {{ $t("message.externalChanges.automaticChange") }}
        </p>
      </v-col>

      <v-col cols="6" v-if="change && change.type && change.field">
        <component :change="change" :is="dynamicChangeComponent" />
      </v-col>
    </template>
    <template v-else>
      <v-col cols="2">
        <div v-if="changeDetectedDate">
          <span class="date">{{ changeDetectedDate }}</span>
          <span class="date-label">
            {{ $t("message.externalChanges.changeDetected") }}
          </span>
        </div>
      </v-col>

      <v-col cols="2" v-if="change && change.source">
        {{ $t(`message.externalChangesSources.${change.source}`) }}
      </v-col>

      <v-col cols="5" v-if="change && change.type && change.field">
        <component :change="change" :is="dynamicChangeComponent" />
      </v-col>

      <v-col cols="3">
        <template v-if="canAcceptAndRejectExternalChanges">
          <v-tooltip top max-width="550" :disabled="!facilityProcessing">
            <template v-slot:activator="{ on }">
              <div class="actions" v-on="on">
                <v-icon
                  small
                  color="accent"
                  class="ml-1"
                  v-if="facilityProcessing"
                >
                  mdi-information-outline
                </v-icon>
                <v-tooltip
                  v-if="isInformationalField && !facilityProcessing"
                  top
                >
                  <template v-slot:activator="{ on }">
                    <v-icon small color="accent" v-on="on" class="ml-1">
                      mdi-information-outline
                    </v-icon>
                  </template>
                  <span>{{
                    $t("message.externalChanges.informationalFieldTooltip")
                  }}</span>
                </v-tooltip>
                <ui-button
                  :disabled="facilityProcessing"
                  color="primary"
                  @click="accept"
                >
                  {{
                    isInformationalField
                      ? $t("message.common.ok")
                      : $t("message.common.accept")
                  }}
                </ui-button>
                <ui-button
                  v-if="!isInformationalField"
                  :disabled="facilityProcessing"
                  color="primary"
                  outlined
                  @click="reject"
                >
                  {{ $t("message.common.reject") }}
                </ui-button>
              </div>
            </template>
            <span class="pre-line">{{
              $t("message.externalChanges.processingInProgressTooltip")
            }}</span>
          </v-tooltip>
        </template>
      </v-col>
    </template>
  </v-row>
</template>

<script>
import moment from "moment";
import { FORMAT } from "@/config/date-format";
import { EXTERNAL_CHANGES_FIELD } from "@/enums/external-changes";
import { Permission } from "@/enums/permission";
import { permissionCheck } from "@/helpers/permission-check";

import OpeningHoursChange from "@/components/ExternalChanges/changes/OpeningHoursChange.vue";
import SpecialOpeningHoursChange from "@/components/ExternalChanges/changes/SpecialOpeningHoursChange.vue";
import OpeningDateChange from "@/components/ExternalChanges/changes/OpeningDateChange.vue";
import OpenStatusChange from "@/components/ExternalChanges/changes/OpenStatusChange.vue";
import CategoryChange from "@/components/ExternalChanges/changes/CategoryChange.vue";
import LocationStateInformation from "@/components/ExternalChanges/changes/LocationStateInformation.vue";
import AttributeChange from "@/components/ExternalChanges/changes/AttributeChange.vue";
import ServiceChange from "@/components/ExternalChanges/changes/ServiceChange.vue";
import LabelsChange from "@/components/ExternalChanges/changes/LabelsChange.vue";
import MoreHoursChange from "@/components/ExternalChanges/changes/MoreHoursChange.vue";
import StandardChange from "@/components/ExternalChanges/changes/StandardChange.vue";

export default {
  name: "ChangeListItem",
  props: {
    change: {
      type: Object,
      required: true
    },
    isHistoryMode: {
      type: Boolean,
      default: false
    },
    facilityProcessing: {
      type: Boolean,
      default: false
    }
  },
  components: {
    OpeningHoursChange,
    SpecialOpeningHoursChange,
    OpeningDateChange,
    OpenStatusChange,
    CategoryChange,
    LocationStateInformation,
    AttributeChange,
    LabelsChange,
    MoreHoursChange,
    StandardChange,
    ServiceChange
  },
  computed: {
    dynamicChangeComponent() {
      if (!this.change || !this.change.field) return null;
      switch (this.change.field.value) {
        case EXTERNAL_CHANGES_FIELD.REGULAR_HOURS:
          return "OpeningHoursChange";
        case EXTERNAL_CHANGES_FIELD.SPECIAL_HOURS:
          return "SpecialOpeningHoursChange";
        case EXTERNAL_CHANGES_FIELD.OPENING_DATE:
          return "OpeningDateChange";
        case EXTERNAL_CHANGES_FIELD.OPEN_STATUS:
          return "OpenStatusChange";
        case EXTERNAL_CHANGES_FIELD.LOCATION_STATE:
          return "LocationStateInformation";
        case EXTERNAL_CHANGES_FIELD.PRIMARY_CATEGORY:
          return "CategoryChange";
        case EXTERNAL_CHANGES_FIELD.ADDITIONAL_CATEGORIES:
          return "CategoryChange";
        case EXTERNAL_CHANGES_FIELD.ATTRIBUTES:
          return "AttributeChange";
        case EXTERNAL_CHANGES_FIELD.SERVICES:
          return "ServiceChange";
        case EXTERNAL_CHANGES_FIELD.LABELS:
          return "LabelsChange";
        case EXTERNAL_CHANGES_FIELD.MORE_HOURS:
          return "MoreHoursChange";
        default:
          return "StandardChange";
      }
    },
    isInformationalField() {
      const informationalFields = [EXTERNAL_CHANGES_FIELD.LOCATION_STATE];
      return informationalFields.includes(this.change.field.value);
    },
    canAcceptAndRejectExternalChanges() {
      return permissionCheck(Permission.APPROVE_AND_REJECT_EXTERNAL_CHANGES);
    },
    changeDetectedDate() {
      return (
        this.change &&
        this.change.created_at &&
        moment(this.change.created_at).format(FORMAT.DATE)
      );
    },
    lastActionDate() {
      return (
        this.change &&
        this.change.updated_at &&
        moment(this.change.updated_at).format(FORMAT.DATE)
      );
    },
    actionUpperLabel() {
      const prefix =
        this.change && this.change.author === null
          ? `${this.$t("message.common.automatically")} `
          : "";
      const actionText = this.change
        ? this.$t(`message.externalChangesStatuses.${this.change.status}`)
        : "";
      return this.change
        ? `${prefix}${prefix ? actionText.toLowerCase() : actionText} ${this.$t(
            "message.common.by"
          )}`
        : "";
    }
  },
  methods: {
    accept() {
      this.$emit("accept", this.change.id);
    },
    reject() {
      this.$emit("reject", this.change.id);
    }
  }
};
</script>

<style lang="scss" scoped>
.wrapper {
  padding: $spacing 0;
  @include font-size(14px);
}

.actions {
  display: flex;
  align-items: center;

  & > *:not(:last-child) {
    margin-right: $spacing;
  }
}

p {
  margin-bottom: 0;
  font-weight: 500;

  &:first-child {
    margin-bottom: $spacing * 0.5;
  }
}

.date {
  color: $primary;
  @include font-size(14px);
  font-weight: 600;
  margin-right: $spacing * 0.5;
}

.date-label {
  @include font-size(12px);
  font-weight: 400;
}

.author {
  font-weight: 600;
}

.auto-change {
  color: $primary;
}
</style>
